import { ReactNode } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { ImageActionMenu } from "./ImageActionMenu";
import { useVideoVersions } from "@/hooks/useVideoVersions";
import { VideoToolbarControls, VideoPlayerOverlay, VideoPromptDialog } from "./VideoControls";

export interface ImageLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  extraContent?: ReactNode;
  /**
   * Master visual id. When provided (together with workspace/user), video
   * generation is enabled and versions persist to `visuals.videos`.
   */
  visualId?: string;
  workspaceId?: string;
  userId?: string;
}

export function ImageLightbox({
  isOpen,
  onClose,
  imageUrl,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  onEdit,
  onDelete,
  extraContent,
  visualId,
  workspaceId,
  userId,
}: ImageLightboxProps) {
  const actions = [
    ...(onDelete ? [{ label: "Delete", icon: <Trash2 className="w-4 h-4" />, onClick: () => { onClose(); onDelete(); }, destructive: true }] : []),
  ];

  // Masters are generated 16:9 at 1K — matching generate-render's imageConfig.
  const video = useVideoVersions({
    table: "visuals",
    rowId: visualId,
    isOpen,
    imageUrl,
    workspaceId,
    userId,
    formatRatio: "16:9",
    formatWidth: 1920,
    formatHeight: 1080,
  });

  const videoEnabled = !!visualId;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-background/95 backdrop-blur-lg border-border">
        <VisuallyHidden><DialogTitle>Image Preview</DialogTitle></VisuallyHidden>
        <div className="relative flex flex-col items-center justify-center min-h-[80vh]">
          {/* Top-left controls */}
          <div className="absolute top-4 left-4 z-50 flex items-center gap-2">
            <ImageActionMenu actions={actions} />
            {videoEnabled && <VideoToolbarControls video={video} className="bg-background/85 hover:bg-background backdrop-blur-sm h-9 text-xs shadow-md" />}
          </div>

          {/* Previous button */}
          {hasPrevious && onPrevious && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 z-50 bg-background/50 hover:bg-background/80 top-1/2 -translate-y-1/2"
              onClick={onPrevious}
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
          )}

          {/* Image + video overlay share a positioned wrapper so the video
              lines up exactly over the still. */}
          <div className="relative inline-block">
            <img
              src={imageUrl}
              alt="Full size render"
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            {videoEnabled && (
              <VideoPlayerOverlay video={video} formatWidth={1920} formatHeight={1080} />
            )}
          </div>

          {/* Version thumbnails or extra content */}
          {extraContent}

          {/* Next button */}
          {hasNext && onNext && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 z-50 bg-background/50 hover:bg-background/80 top-1/2 -translate-y-1/2"
              onClick={onNext}
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {videoEnabled && <VideoPromptDialog video={video} />}
    </>
  );
}
